import { useState } from 'react';
import Stepper from '../../components/common/Stepper';
import {
  MemberDTO,
} from '../../services/apiClient';
import PersonalInfo from '../../components/steps/member-join/PersonalInfo';
import ContactInfo from '../../components/steps/member-join/ContactInfo';
import AddressDetails from '../../components/steps/member-join/AddressDetails';
import ReferralSource from '../../components/steps/member-join/ReferralSource';
import AccountSetup from '../../components/steps/member-join/AccountSetup';
import SimpleAlert from '../../components/common/SimpleAlert';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useLogin } from '../../hooks/useLogin';
import { ALLOW_ONLY_ADMIN_LOGIN } from '../../config/config';
import OptInServices from '../../components/steps/member-join/OptInServices';

const Join: React.FC = () => {
  const [member, setMember] = useState<MemberDTO>({} as MemberDTO);
  const [steps, setSteps] = useState(['', '', '', '', 'Finish']);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';
  const { create, isLoading } = useMemberApi();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { handleLogin, loading: loginLoading } = useLogin();

  ////////////////////////////////////
  /// FUNCTIONS
  ////////////////////////////////////

  ////////////////////////////////////
  /// EVENTS
  ////////////////////////////////////

  const handleChange = (updated: Partial<MemberDTO>) => {
    setMember((prev) => ({ ...prev, ...updated }) as MemberDTO);
  };

  const handleOptInChange = (updated: Partial<MemberDTO>) => {
    setMember((prev) => ({ ...prev, ...updated }) as MemberDTO);
  };


  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await create(member);

      if (member.email && member.password)
        handleLogin(member.email, member.password);
    } catch (error) {
      console.error('Failed to create member', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  ////////////////////////////////////
  /// HOOKS
  ////////////////////////////////////

  ////////////////////////////////////
  /// RENDERS & HTML
  ////////////////////////////////////

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfo
            member={member || ({} as MemberDTO)}
            onChange={handleChange}
            onNext={() => setCurrentStep(2)}
          />
        );
      case 2:
        return (
          <ContactInfo
            member={member || ({} as MemberDTO)}
            onChange={handleChange}
            onBack={() => setCurrentStep(1)}
            onNext={() => setCurrentStep(3)}
          />
        );
      case 3:
        return (
          <AddressDetails
            member={member || ({} as MemberDTO)}
            onChange={handleChange}
            onBack={() => setCurrentStep(2)}
            onNext={() => setCurrentStep(4)}
          />
        );
      case 4:
        return (
          <ReferralSource
            member={member || ({} as MemberDTO)}
            onChange={handleChange}
            onBack={() => setCurrentStep(3)}
            onNext={() => setCurrentStep(5)}
          />
        );

      case 5:
        return (
          <OptInServices
            member={member || ({} as MemberDTO)}
            onChange={handleOptInChange}
            onBack={() => setCurrentStep(4)}
            onNext={() => setCurrentStep(6)}
          />
        );

      case 6:
        return (
          <AccountSetup
            member={member || ({} as MemberDTO)}
            onChange={handleChange}
            onBack={() => setCurrentStep(5)}
            onSubmit={handleSubmit}
            isLoading={loginLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
      <div
        className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg text-center z-10">
        <img
          src="/images/logo-linnear-navy.png"
          alt="Natshoot Logo"
          className="mx-auto mb-6 w-56"
        />

        {ALLOW_ONLY_ADMIN_LOGIN ? (
          <SimpleAlert
            variant="info"
            message={
              <div>
                <p className="font-semibold mb-2">New Registrations Temporarily Unavailable</p>
                <p className="mb-2">
                  The Natshoot system is currently offline while we resolve identified technical issues following our recent launch. New member registrations are temporarily disabled.
                </p>
                <p className="mb-2">
                  The system will be back online on <strong>Monday, 2026/04/13</strong>.
                </p>
                <p>
                  For support please send an email to{' '}
                  <a
                    href="mailto:member@natshoot.co.za"
                    className="underline font-semibold"
                  >
                    member@natshoot.co.za
                  </a>
                </p>
              </div>
            }
          />
        ) : (
          <>
            <Stepper currentStep={currentStep} steps={steps} />
            <div className="mt-5 sm:mt-8">{renderStepContent()}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default Join;
